import { SOUND_GAINS, type SoundId } from '@electron/shared/sounds.types';
import { observable, runInAction } from 'mobx';

import { withTimeout } from '@/lib/promise';
import { createAudioContext } from '@/utils/audio-context';

import askUrl from '../../../build-resources/assets/audio/ask.wav';
import attentionUrl from '../../../build-resources/assets/audio/attention.wav';
import discardUrl from '../../../build-resources/assets/audio/discard.wav';
import doneUrl from '../../../build-resources/assets/audio/done.wav';
import helloUrl from '../../../build-resources/assets/audio/hello.mp3';
import listenCloseUrl from '../../../build-resources/assets/audio/listen-close.wav';
import listenOpenUrl from '../../../build-resources/assets/audio/listen-open.wav';
import nothingUrl from '../../../build-resources/assets/audio/nothing.wav';
import problemUrl from '../../../build-resources/assets/audio/problem.wav';
import signOffUrl from '../../../build-resources/assets/audio/sign-off.mp3';

import { createSongbird } from './birdsong';

export type { SoundId };

const SOUND_FILE_URLS: Record<SoundId, string> = {
  'listen-open': listenOpenUrl,
  'listen-close': listenCloseUrl,
  discard: discardUrl,
  done: doneUrl,
  nothing: nothingUrl,
  problem: problemUrl,
  attention: attentionUrl,
  ask: askUrl,
  'sign-off': signOffUrl,
  hello: helloUrl,
};

/** Longer than the longest asset (`hello`, 1.94s) so only a stuck context hits it */
const PLAYBACK_TIMEOUT_MS = 5_000;
/** Long enough that cutting a sound short fades rather than clicks */
const REPLACE_FADE_SECONDS = 0.015;
const CHIRP_GAIN = 0.09;
const CHIRP_FADE_SECONDS = 0.2;
const IDLE_CHIRP_GAP_MIN_MS = 10_000;
const IDLE_CHIRP_GAP_MAX_MS = 20_000;
const STEP_CHIRP_MIN_GAP_MS = 8_000;

type PlayingSound = { gain: GainNode; source: AudioBufferSourceNode };

let sharedContext: AudioContext | null = null;
const decodedBuffers = new Map<SoundId, AudioBuffer>();
/**
 * One sound at a time. Cues report stages of the same action - a recording
 * closes, then reports no speech - so a later cue always supersedes the one
 * still sounding rather than layering over it.
 */
let playingSound: PlayingSound | null = null;
/**
 * Which cue supersedes which is decided when it is asked for, not when its
 * buffer happens to finish loading - an uncached asset can otherwise decode
 * after a cue requested later and start over the top of it.
 */
let latestRequestId = 0;
let chirpMeter: MediaStreamAudioDestinationNode | null = null;
const songbird = createSongbird();
let activeChirps: ThinkingChirps | null = null;
const thinkingChirpsStream = observable.box<MediaStream | null>(null, {
  deep: false,
});

function getSharedContext(): AudioContext | null {
  sharedContext ??= createAudioContext();
  return sharedContext;
}

function fadeOut(
  context: AudioContext,
  sound: PlayingSound,
  fadeSeconds: number
): void {
  const fadeEndsAt = context.currentTime + fadeSeconds;
  sound.gain.gain.cancelScheduledValues(context.currentTime);
  sound.gain.gain.setValueAtTime(sound.gain.gain.value, context.currentTime);
  sound.gain.gain.linearRampToValueAtTime(0, fadeEndsAt);
  sound.source.stop(fadeEndsAt);
}

function replacePlayingSound(context: AudioContext, next: PlayingSound): void {
  const previous = playingSound;
  playingSound = next;
  if (previous) fadeOut(context, previous, REPLACE_FADE_SECONDS);
}

async function loadBuffer(
  context: AudioContext,
  id: SoundId
): Promise<AudioBuffer> {
  const cached = decodedBuffers.get(id);
  if (cached) return cached;

  const response = await fetch(SOUND_FILE_URLS[id]);
  const buffer = await context.decodeAudioData(await response.arrayBuffer());
  decodedBuffers.set(id, buffer);
  return buffer;
}

async function playThroughContext(
  context: AudioContext,
  id: SoundId,
  gain: GainNode,
  source: AudioBufferSourceNode,
  isAbandoned: () => boolean
): Promise<void> {
  // Autoplay policy leaves a context created before any user gesture suspended
  if (context.state === 'suspended') await context.resume();
  if (isAbandoned()) return;

  const buffer = await loadBuffer(context, id);
  if (isAbandoned()) return;

  source.buffer = buffer;
  gain.connect(context.destination);
  source.connect(gain);

  await new Promise<void>((resolve) => {
    source.addEventListener('ended', () => resolve(), { once: true });
    replacePlayingSound(context, { gain, source });
    source.start();
  });
}

/** Always settles: a suspended context or a missing `ended` event resolves at the deadline */
export async function playSound(id: SoundId, volume = 1): Promise<void> {
  const context = getSharedContext();
  if (!context) return;

  const requestId = ++latestRequestId;
  let gain: GainNode | null = null;
  let source: AudioBufferSourceNode | null = null;
  let abandoned = false;
  const isAbandoned = () => abandoned || requestId !== latestRequestId;

  try {
    gain = context.createGain();
    gain.gain.value = SOUND_GAINS[id] * volume;
    source = context.createBufferSource();
    await withTimeout(
      playThroughContext(context, id, gain, source, isAbandoned),
      PLAYBACK_TIMEOUT_MS
    );
  } catch {
    // Best effort: playback must not surface failures to the caller
  } finally {
    // A resume or decode outliving the deadline must not connect and play late
    abandoned = true;
    if (playingSound?.source === source) playingSound = null;
    source?.disconnect();
    gain?.disconnect();
  }
}

export function getThinkingChirpsStream(): MediaStream | null {
  return thinkingChirpsStream.get();
}

export type ThinkingChirps = { chirpForStep(): void; stop(): void };

export function startThinkingChirps(
  quietMs: number,
  volume = 1
): ThinkingChirps {
  activeChirps?.stop();
  const context = getSharedContext();
  if (!context) return { chirpForStep: () => {}, stop: () => {} };

  chirpMeter ??= context.createMediaStreamDestination();
  const meter = chirpMeter;
  const startedAt = performance.now();
  let lastChirpAt = -Infinity;
  let stopped = false;
  let idleChirpTimer: ReturnType<typeof setTimeout> | undefined;
  let chirp: PlayingSound | null = null;

  const playChirp = async (isAbandoned: () => boolean) => {
    if (context.state === 'suspended') await context.resume();
    if (isAbandoned()) return;

    const samples = songbird.sing(context.sampleRate);
    const buffer = context.createBuffer(1, samples.length, context.sampleRate);
    buffer.copyToChannel(samples, 0);
    const gain = context.createGain();
    gain.gain.value = CHIRP_GAIN * volume;
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.addEventListener(
      'ended',
      () => {
        if (playingSound?.source === source) playingSound = null;
        source.disconnect();
        gain.disconnect();
      },
      { once: true }
    );
    gain.connect(context.destination);
    source.connect(gain);
    source.connect(meter);
    chirp = { gain, source };
    replacePlayingSound(context, chirp);
    source.start();
  };

  const chirpNow = () => {
    lastChirpAt = performance.now();
    const requestId = ++latestRequestId;
    let timedOut = false;
    const isAbandoned = () =>
      stopped || timedOut || requestId !== latestRequestId;
    withTimeout(playChirp(isAbandoned), PLAYBACK_TIMEOUT_MS).catch(() => {
      timedOut = true;
    });
    scheduleIdleChirp(
      IDLE_CHIRP_GAP_MIN_MS +
        Math.random() * (IDLE_CHIRP_GAP_MAX_MS - IDLE_CHIRP_GAP_MIN_MS)
    );
  };

  const scheduleIdleChirp = (delayMs: number) => {
    clearTimeout(idleChirpTimer);
    idleChirpTimer = setTimeout(chirpNow, delayMs);
  };

  const chirps: ThinkingChirps = {
    chirpForStep: () => {
      const now = performance.now();
      const isQuiet = now - startedAt < quietMs;
      const chirpedRecently = now - lastChirpAt < STEP_CHIRP_MIN_GAP_MS;
      if (stopped || isQuiet || chirpedRecently) return;
      chirpNow();
    },
    stop: () => {
      stopped = true;
      clearTimeout(idleChirpTimer);
      if (activeChirps === chirps) {
        activeChirps = null;
        runInAction(() => thinkingChirpsStream.set(null));
      }
      if (!chirp || playingSound?.source !== chirp.source) return;
      playingSound = null;
      fadeOut(context, chirp, CHIRP_FADE_SECONDS);
    },
  };
  activeChirps = chirps;
  runInAction(() => thinkingChirpsStream.set(meter.stream));
  scheduleIdleChirp(quietMs);
  return chirps;
}
